import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import RowDeleteButton from "@/components/app/RowDeleteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Sparkles, Layers, Copy, Search } from "lucide-react";
import { toast } from "sonner";

const SYSTEM_TYPES = ["On-Grid", "Hybrid", "Off-Grid"];
const PACKAGE_TYPES = ["Standard", "Premium", "Commercial"];

export default function SystemTemplates() {
  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = () => api.get("/system-templates").then((r) => setTemplates(r.data));
  useEffect(() => { load(); }, []);

  const duplicate = async (t) => {
    // Suggest a unique name; user can rename later via Edit.
    const base = `${t.name} (Copy)`;
    let candidate = base;
    let n = 2;
    const taken = new Set(templates.map((x) => x.name));
    while (taken.has(candidate)) {
      candidate = `${base} ${n}`;
      n += 1;
    }
    if (!window.confirm(`Create a duplicate template named "${candidate}"? You can rename it after.`)) return;
    try {
      const payload = {
        name: candidate,
        capacity_kw: t.capacity_kw,
        system_type: t.system_type,
        package_type: t.package_type,
        package_price_inr: t.package_price_inr,
        items: (t.items || []).map((it) => ({
          name: it.name,
          description: it.description || "",
          quantity: it.quantity || "1",
          qty_num: Number(it.qty_num || 1),
          price_inr: Number(it.price_inr || 0),
          cgst_pct: Number(it.cgst_pct || 0),
          sgst_pct: Number(it.sgst_pct || 0),
          igst_pct: Number(it.igst_pct || 0),
        })),
      };
      const r = await api.post("/system-templates", payload);
      toast.success(`Duplicated as "${candidate}"`);
      await load();
      setActiveId(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    }
  };

  const seed = async () => {
    if (!window.confirm("Insert the 9 default system templates? Existing names will be skipped.")) return;
    try {
      const r = await api.post("/system-templates/seed");
      toast.success(`Added ${r.data.seeded} templates (total ${r.data.total})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const active = templates.find((t) => t.id === activeId);

  // Search over template name, system type and package type — case-insensitive
  // substring match. Empty query returns everything.
  const filteredTemplates = (() => {
    const s = q.trim().toLowerCase();
    if (!s) return templates;
    return templates.filter((t) => (
      String(t.name || "").toLowerCase().includes(s) ||
      String(t.system_type || "").toLowerCase().includes(s) ||
      String(t.package_type || "").toLowerCase().includes(s)
    ));
  })();

  return (
    <div data-testid="system-templates-page">
      <PageHeader
        eyebrow="Master Data"
        title="System Templates"
        subtitle="Define the solar system packages your sales team can pick when generating proposals. Each template carries a bill of materials, capacity, system type, and package price."
        action={
          <div className="flex gap-2">
            <Button onClick={seed} variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-11 px-4" data-testid="seed-templates">
              <Sparkles className="w-4 h-4 mr-2" /> Load 9 Defaults
            </Button>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="new-template-button">
                  <Plus className="w-4 h-4 mr-2" /> New Template
                </Button>
              </DialogTrigger>
              <TemplateEditor
                onDone={() => { setCreateOpen(false); load(); }}
              />
            </Dialog>
          </div>
        }
      />

      <div className="px-4 sm:px-8 py-6 space-y-4">
        {templates.length > 0 && (
          <div className="relative w-full sm:w-96">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, system type or package…"
              className="rounded-none h-10 pl-9"
              data-testid="templates-search"
            />
          </div>
        )}
        {templates.length === 0 ? (
          <div className="border border-dashed border-[#E5E7EB] text-center py-16">
            <Layers className="w-8 h-8 mx-auto mb-3 text-[#9CA3AF]" />
            <div className="font-bold mb-1">No templates yet</div>
            <div className="text-xs text-[#4B5563] mb-4">Click "Load 9 Defaults" to seed the standard catalog, or "New Template" to build your own.</div>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="border border-dashed border-[#E5E7EB] text-center py-10 text-xs text-[#4B5563]">
            No templates match "<span className="font-bold">{q}</span>". Try a different word.
          </div>
        ) : (
          <div className="border border-[#E5E7EB] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr>
                  {["Template", "Capacity", "System Type", "Package", "Items", "Price", "", "", ""].map((h, i) => (
                    <th key={i} className="text-left label-eyebrow !text-[9px] px-3 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((t) => (
                  <tr key={t.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`tpl-row-${t.id}`}>
                    <td className="px-3 py-2"><div className="font-semibold">{t.name}</div></td>
                    <td className="px-3 py-2 font-bold">{t.capacity_kw} kW</td>
                    <td className="px-3 py-2 text-xs">{t.system_type}</td>
                    <td className="px-3 py-2 text-xs">{t.package_type}</td>
                    <td className="px-3 py-2 text-xs">{(t.items || []).length}</td>
                    <td className="px-3 py-2 font-bold">{inr(t.package_price_inr)}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setActiveId(t.id)} className="text-[10px] font-bold text-[#002FA7] hover:underline uppercase tracking-widest" data-testid={`tpl-edit-${t.id}`}>Edit</button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => duplicate(t)}
                        className="inline-flex items-center gap-1 text-[10px] font-bold text-[#0a0a0a] hover:text-[#002FA7] hover:underline uppercase tracking-widest"
                        data-testid={`tpl-duplicate-${t.id}`}
                        title="Duplicate this template"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <RowDeleteButton
                        endpoint={`/system-templates/${t.id}`}
                        confirmMessage={`Delete template "${t.name}"? Existing proposals using it will keep their snapshot.`}
                        onDeleted={load}
                        testid={`tpl-delete-${t.id}`}
                        successMessage="Template deleted"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!activeId} onOpenChange={(o) => !o && setActiveId(null)}>
        {active && (
          <TemplateEditor
            template={active}
            onDone={() => { setActiveId(null); load(); }}
            onDelete={() => { setActiveId(null); load(); }}
          />
        )}
      </Dialog>
    </div>
  );
}

// =====================================================================
//  Template editor (create + edit) — shared dialog
// =====================================================================
function TemplateEditor({ template, onDone, onDelete }) {
  const isEdit = Boolean(template);
  const [form, setForm] = useState({
    name: template?.name || "",
    capacity_kw: template?.capacity_kw ?? 3,
    system_type: template?.system_type || "On-Grid",
    package_type: template?.package_type || "Standard",
    package_price_inr: template?.package_price_inr ?? 0,
    items: (template?.items || []).map((it) => ({ ...it })),
  });
  const set = (k) => (v) => setForm({ ...form, [k]: v.target ? v.target.value : v });

  const addItem = () => setForm({ ...form, items: [...form.items, { name: "", description: "", quantity: "1", qty_num: 1, price_inr: 0, cgst_pct: 9, sgst_pct: 9, igst_pct: 0 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };

  // Per-line-item computation + template grand total. `qty_num` is the numeric
  // qty used for math; `quantity` is a free-form label (e.g. "1 Set"). Prices
  // are GST-inclusive per unit; taxes are computed by backing out from the
  // total tax %.
  const lineTotal = (it) => {
    const qty = Number(it.qty_num || 0);
    const price = Number(it.price_inr || 0);
    return qty * price;
  };
  const grandTotal = form.items.reduce((sum, it) => sum + lineTotal(it), 0);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    // Keep any row that has EITHER a name OR a non-zero rate — this stops the
    // template from silently dropping items when a user typed the numbers but
    // forgot the Name field (previously the whole row disappeared while the
    // grand total still ticked up, leading to templates with a Package Price
    // but zero items). Blanks are auto-named "Item N" so nothing goes missing.
    const cleaned = form.items
      .map((it) => ({ ...it, name: (it.name || "").trim() }))
      .filter((it) => it.name || Number(it.price_inr || 0) > 0)
      .map((it, idx) => {
        const qNum = Number(it.qty_num || 1);
        return {
          ...it,
          name: it.name || `Item ${idx + 1}`,
          // Keep the free-form `quantity` string in sync with the numeric
          // `qty_num` so downstream displays (PDF, customer link, previews)
          // can rely on either field.
          quantity: qNum === Math.floor(qNum) ? String(Math.floor(qNum)) : String(qNum),
          qty_num: qNum,
          price_inr: Number(it.price_inr || 0),
          cgst_pct: Number(it.cgst_pct || 0),
          sgst_pct: Number(it.sgst_pct || 0),
          igst_pct: Number(it.igst_pct || 0),
        };
      });
    const autoNamed = cleaned.filter((it, i) => it.name === `Item ${i + 1}` && !(form.items[i]?.name || "").trim()).length;
    const payload = {
      ...form,
      capacity_kw: parseFloat(form.capacity_kw) || 0,
      // Package Price mirrors the item grand-total so the two never drift.
      package_price_inr: grandTotal,
      items: cleaned,
    };
    try {
      if (isEdit) {
        await api.patch(`/system-templates/${template.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/system-templates", payload);
        toast.success("Template created");
      }
      if (autoNamed > 0) {
        toast.info(`${autoNamed} row${autoNamed === 1 ? "" : "s"} were auto-named because the Item field was blank`);
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${template.name}"? Existing proposals using this template will keep their snapshot.`)) return;
    try {
      await api.delete(`/system-templates/${template.id}`);
      toast.success("Template deleted");
      onDelete();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-editor">
      <DialogHeader>
        <DialogTitle className="font-black text-2xl tracking-tight">
          {isEdit ? "Edit Template" : "New Template"}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4 pt-2">
        <Input value={form.name} onChange={set("name")} placeholder="Template name (e.g. 5kW On-Grid Premium)"
               className="rounded-none h-11 font-bold text-lg" data-testid="tpl-name" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Capacity (kW)" type="number" value={form.capacity_kw} onChange={set("capacity_kw")} testid="tpl-capacity" />
          <SelectField label="System Type" value={form.system_type} onChange={set("system_type")} options={SYSTEM_TYPES} testid="tpl-system-type" />
          <SelectField label="Package Type" value={form.package_type} onChange={set("package_type")} options={PACKAGE_TYPES} testid="tpl-package-type" />
          {/* Package Price is derived from the sum of line totals (qty × price
              per row) — no manual entry needed. Displays as a read-only pill
              so the creator sees the number update live as they build the BOM. */}
          <div>
            <Label className="label-eyebrow">Package Price (₹)</Label>
            <div
              className="rounded-none mt-2 h-10 border border-[#E5E7EB] bg-[#F9FAFB] px-3 flex items-center justify-between text-sm font-black tracking-tight"
              data-testid="tpl-price"
            >
              <span>{inr(grandTotal)}</span>
              <span className="text-[9px] uppercase tracking-widest font-bold text-[#4B5563]">Auto</span>
            </div>
          </div>
        </div>

        {/* LINE ITEMS */}
        <div className="border border-[#E5E7EB] p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-eyebrow">Bill of Materials</div>
              <div className="text-xs text-[#4B5563]">{form.items.length} item{form.items.length === 1 ? "" : "s"}</div>
            </div>
            <Button onClick={addItem} size="sm" variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-8" data-testid="tpl-add-item">
              <Plus className="w-3 h-3 mr-1" /> Add Item
            </Button>
          </div>

          {form.items.length === 0 ? (
            <div className="text-xs text-[#9CA3AF] py-6 text-center border border-dashed border-[#E5E7EB]">
              No items yet. Click "Add Item" to define what's included.
            </div>
          ) : (
            <div className="space-y-3">
              {form.items.map((it, idx) => (
                <div key={idx} className="border border-[#E5E7EB] p-3 space-y-2" data-testid={`tpl-item-${idx}`}>
                  {/* Row 1 — name + description + delete */}
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-4">
                      <div className="label-eyebrow !text-[9px] mb-1">Item <span className="text-[#DC2626]">*</span></div>
                      <Input
                        value={it.name}
                        onChange={(e) => updateItem(idx, "name", e.target.value)}
                        placeholder="e.g. Solar Panel"
                        className={`rounded-none h-9 ${!it.name && Number(it.price_inr || 0) > 0 ? "border-[#DC2626] bg-[#FEF2F2]" : ""}`}
                        data-testid={`tpl-item-name-${idx}`}
                      />
                    </div>
                    <div className="col-span-7">
                      <div className="label-eyebrow !text-[9px] mb-1">Description</div>
                      <Textarea
                        value={it.description || ""}
                        onChange={(e) => updateItem(idx, "description", e.target.value)}
                        placeholder="e.g. 590Wp Mono PERC"
                        rows={1}
                        className="rounded-none min-h-[36px]"
                        data-testid={`tpl-item-desc-${idx}`}
                      />
                    </div>
                    <button
                      onClick={() => removeItem(idx)}
                      className="col-span-1 h-9 mt-5 flex items-center justify-center text-[#DC2626] hover:bg-[#FEF2F2] border border-[#E5E7EB]"
                      title="Remove item"
                      data-testid={`tpl-item-remove-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {/* Row 2 — qty · rate · CGST · SGST · IGST · line total.
                      Uses `inputMode="decimal"` on plain text inputs so mobile
                      keyboards drop to number pad but no browser-drawn spinner
                      arrows appear (per user request). */}
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-2">
                      <div className="label-eyebrow !text-[9px] mb-1">Qty</div>
                      <Input
                        value={it.qty_num ?? 1}
                        onChange={(e) => updateItem(idx, "qty_num", e.target.value.replace(/[^\d.]/g, ""))}
                        inputMode="decimal"
                        placeholder="1"
                        className="rounded-none h-9"
                        data-testid={`tpl-item-qty-${idx}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <div className="label-eyebrow !text-[9px] mb-1">Rate ₹ (incl. GST)</div>
                      <Input
                        value={it.price_inr ?? 0}
                        onChange={(e) => updateItem(idx, "price_inr", e.target.value.replace(/[^\d.]/g, ""))}
                        inputMode="decimal"
                        placeholder="0"
                        className="rounded-none h-9"
                        data-testid={`tpl-item-price-${idx}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <div className="label-eyebrow !text-[9px] mb-1">CGST %</div>
                      <Input
                        value={it.cgst_pct ?? 0}
                        onChange={(e) => updateItem(idx, "cgst_pct", e.target.value.replace(/[^\d.]/g, ""))}
                        inputMode="decimal"
                        placeholder="0"
                        className="rounded-none h-9 text-xs"
                        data-testid={`tpl-item-cgst-${idx}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <div className="label-eyebrow !text-[9px] mb-1">SGST %</div>
                      <Input
                        value={it.sgst_pct ?? 0}
                        onChange={(e) => updateItem(idx, "sgst_pct", e.target.value.replace(/[^\d.]/g, ""))}
                        inputMode="decimal"
                        placeholder="0"
                        className="rounded-none h-9 text-xs"
                        data-testid={`tpl-item-sgst-${idx}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <div className="label-eyebrow !text-[9px] mb-1">IGST %</div>
                      <Input
                        value={it.igst_pct ?? 0}
                        onChange={(e) => updateItem(idx, "igst_pct", e.target.value.replace(/[^\d.]/g, ""))}
                        inputMode="decimal"
                        placeholder="0"
                        className="rounded-none h-9 text-xs"
                        data-testid={`tpl-item-igst-${idx}`}
                      />
                    </div>
                    <div className="col-span-4 text-right">
                      <div className="label-eyebrow !text-[9px] mb-1">Line Total</div>
                      <div className="text-sm font-black tracking-tight py-2 border border-transparent" data-testid={`tpl-item-linetotal-${idx}`}>
                        {inr(lineTotal(it))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {/* Grand total footer — sum of qty × price across every row.
                  This value is also mirrored into the top "Package Price"
                  field on save so BOM and headline price never drift. */}
              <div className="border-t-2 border-[#0a0a0a] pt-3 flex items-center justify-between gap-3">
                <div className="label-eyebrow">Items Grand Total (incl. GST)</div>
                <div className="text-xl font-black tracking-tight" data-testid="tpl-grand-total">{inr(grandTotal)}</div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={save} className="flex-1 rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="tpl-save">
            {isEdit ? "Save Changes" : "Create Template"}
          </Button>
          {isEdit && (
            <Button onClick={del} variant="outline" className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] h-11 uppercase tracking-widest text-xs font-bold" data-testid="tpl-delete">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          )}
        </div>
      </div>
    </DialogContent>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  // Plain text inputs by default — for `number`, we swap to `text` +
  // `inputMode="decimal"` so browsers stop drawing spinner arrows (per user
  // request "no need for increment/decrement, only numeric field is required").
  const isNumeric = type === "number";
  const handleChange = isNumeric
    ? (e) => {
        const filtered = e.target.value.replace(/[^\d.]/g, "");
        onChange({ target: { value: filtered } });
      }
    : onChange;
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input
        type={isNumeric ? "text" : type}
        inputMode={isNumeric ? "decimal" : undefined}
        value={value}
        onChange={handleChange}
        className="rounded-none mt-2 h-10"
        data-testid={testid}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="rounded-none mt-2 h-10" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
